class MCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.cartDrawerInner = this.querySelector(".m-cart-drawer__inner");
    this.cartDrawerCloseIcon = this.querySelector(".m-cart-drawer__close");
    this.cartOverlay = this.querySelector(".m-cart__overlay");
    this.rootUrl = window.Shopify.routes.root;
    this.cartUpdateUnsubscriber = undefined;

    this.setHeaderCartIconAccessibility();
    this.cartDrawerCloseIcon.addEventListener("click", this.close.bind(this));
    this.addEventListener("click", (event) => {
      if (event.target.closest(".m-cart-drawer__inner") !== this.cartDrawerInner) {
        this.close();
      }
    });
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, (event) => {
      if (event.source === "main-cart-items") {
        return;
      }
      if (!this.classList.contains("m-cart-drawer--active")) {
        this.open();
      } else {
        this.onCartDrawerUpdate();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll(".m-cart-icon-bubble");
    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");
      cartLink.addEventListener("click", (event) => {
        if (MinimogSettings.enable_cart_drawer) {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // Refresh cart content when drawer opens
    this.onCartDrawerUpdate(false);
    this.classList.add("m-cart-drawer--active");
    requestAnimationFrame(() => {
      this.style.setProperty("--m-bg-opacity", "0.5");
      this.cartDrawerInner.style.setProperty("--translate-x", "0");
    });
    window.MinimogEvents.emit(MinimogTheme.pubSubEvents.openCartDrawer);
    document.documentElement.classList.add("prevent-scroll");
  }

  close() {
    this.style.setProperty("--m-bg-opacity", "0");
    this.cartDrawerInner.style.setProperty("--translate-x", "100%");
    setTimeout(() => {
      this.classList.remove("m-cart-drawer--active");
      document.documentElement.classList.remove("prevent-scroll");
    }, 300);
  }

  renderContents(parsedState) {
    this.classList.contains("m-cart--empty") && this.classList.remove("m-cart--empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (sectionElement && parsedState.sections && parsedState.sections[section.id]) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });
    // Open drawer immediately with the rendered content
    this.open();
  }

  updateCartCount(itemCount) {
    const cartCounts = document.querySelectorAll(".m-cart-count-bubble");
    cartCounts.forEach((cartCount) => {
      if (itemCount > 0) {
        cartCount.textContent = itemCount;
        cartCount.classList.remove("m:hidden");
      } else {
        cartCount.classList.add("m:hidden");
      }
    });
  }

  getCart() {
    return fetchJSON(this.rootUrl + "cart.json");
  }

  initCartDrawerRecommendations() {
    const container = this.querySelector("[data-cart-drawer-recommendations-list]");
    if (!container) return;

    const url = container.dataset.url;
    if (!url) return;

    container.innerHTML = '<div class="m-cart-drawer__recommendations-loading">Loading recommendations...</div>';

    fetch(url, { credentials: "same-origin" })
      .then((response) => response.text())
      .then((text) => {
        let json = null;
        try {
          json = JSON.parse(text);
        } catch (error) {
          json = null;
        }

        if (json && (json.recommendations || json.products)) {
          const products = json.recommendations || json.products;
          container.innerHTML = this.renderCartDrawerRecommendationCards(products.slice(0, 3));
          return;
        }

        const parser = new DOMParser();
        const html = parser.parseFromString(text, "text/html");
        const cards = html.querySelectorAll(".m-product-card");
        if (cards.length) {
          const items = Array.from(cards).slice(0, 3).map((card) => card.outerHTML).join("");
          container.innerHTML = items;
          return;
        }

        container.innerHTML = '<div class="m-cart-drawer__recommendations-loading">No recommendations available right now.</div>';
      })
      .catch(() => {
        container.innerHTML = '<div class="m-cart-drawer__recommendations-loading">Unable to load recommendations.</div>';
      });
  }

  renderCartDrawerRecommendationCards(products) {
    if (!products || !products.length) {
      return '<div class="m-cart-drawer__recommendations-loading">No recommendations available right now.</div>';
    }

    return products
      .map((product) => {
        const image =
          product.featured_image?.src || product.image?.src || product.images?.[0]?.src || "";
        const title = product.title || product.name || "";
        const href = product.url || (product.handle ? `/products/${product.handle}` : "#");
        const priceText = product.price || product.price_min || product.min_price || "";
        return `
          <a href="${href}" class="m-cart-drawer__recommendation-card">
            <div class="m-cart-drawer__recommendation-image">
              ${image ? `<img src="${image}" alt="${title.replace(/"/g, '&quot;')}" loading="lazy" />` : ""}
            </div>
            <div class="m-cart-drawer__recommendation-info">
              <div class="m-cart-drawer__recommendation-name">${title}</div>
              ${priceText ? `<div class="m-cart-drawer__recommendation-price">${priceText}</div>` : ""}
            </div>
          </a>
        `;
      })
      .join("");
  }

  onCartDrawerUpdate(updateFooter = true) {
    fetch(`${MinimogSettings.routes.cart}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        this.getSectionsToRender().forEach((section) => {
          if (section.block === "cart-items") {
            const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            sectionElement.innerHTML = this.getSectionInnerHTML(responseText, section.selector);
          } else {
            if (updateFooter) {
              const sectionElement = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);
              sectionElement.innerHTML = this.getSectionInnerHTML(responseText, section.selector);
            }
          }
        });
        this.initCartDrawerRecommendations();
      })
      .catch((e) => {
        console.error(e);
      });
    this.getCart().then((cart) => {
      this.classList.toggle("m-cart--empty", cart.item_count === 0);
      this.updateCartCount(cart.item_count);
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-items]",
        block: "cart-items",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-cart-subtotal]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
        block: "cart-footer",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("m-cart-drawer", MCartDrawer);

class MCartDrawerItems extends MCartTemplate {
  getSectionsToRender() {
    return [
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-items]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-cart-subtotal]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
      },
    ];
  }
}

customElements.define("m-cart-drawer-items", MCartDrawerItems);
